import React, { useMemo, useState, useEffect, useCallback } from "react";
import moment from "moment";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import {
  BsPeople,
  BsChatDots,
  BsPersonCheck,
  BsGraphUpArrow,
  BsFileEarmarkText,
} from "react-icons/bs";
import whatsappIcon from "../../../assets/images/icono-whatsapp.png";
import messengerIcon from "../../../assets/images/icono-messenger.png";

import {
  Banner,
  BannerContent,
  BannerTitle,
  BannerDescription,
  BannerImage,
  gradients,
  TabsNavigation,
  TabPill,
} from "../../../components/ui";
import useAnalyticsDataEnrichment from "../../../hooks/useAnalyticsDataEnrichment";
import usePermission from "../../../hooks/usePermission";
import PermissionGate from "../../../components/permissionGate";
import ANALYTICS_WIDGET_PERMISSIONS, { ANALYTICS_PERMISSIONS_LIST } from "../../../const/analyticsPermissions";
import ClientAnalyticsWidgets from "./widgets/ClientAnalyticsWidgets.component";
import MessageAnalyticsWidgets from "./widgets/MessageAnalyticsWidgets.component";
import UserPerformanceWidgets from "./widgets/UserPerformanceWidgets.component";
import ClientCampaignAnalyticsDashboard from "./widgets/ClientCampaignAnalyticsDashboard.component";
import { EmptyStateBox } from "./widgets/Widgets.styles";
import { FilterToolbar, FilterGroup, FilterLabel, DatePickerWrapper, DateArrow, DatePickerColumn, DateRangeHelperText, ChannelMuiSelectWrapper } from "./AnalyticsView.styles";
import estadisticasCharacter from "../../../assets/characters-banners/estadisticas.png";
import analyticsDashboardApi from "../../../services/analyticsDashboard.service";
import { downloadAnalyticsReport } from "../../../utils/analyticsExport";

const NoPermissionFallback = (
  <EmptyStateBox>No tienes permiso para ver esta sección.</EmptyStateBox>
);

const BASE_TABS = [
  { id: "clients", label: "Conversaciones", Icon: BsPeople },
  { id: "messages", label: "Mensajes", Icon: BsChatDots },
  { id: "users", label: "Usuarios", Icon: BsPersonCheck },
  { id: "campaigns", label: "Campañas", Icon: BsGraphUpArrow },
];

const TEMPLATE_TAB = { id: "plantillas", label: "Análisis Plantillas", Icon: BsFileEarmarkText };

// Helper: resolve platform icon from channel object
const getPlatformIcon = (channel) => {
  const pName = (channel?.platform?.name || "").toUpperCase();
  if (pName === "MESSENGER" || pName.includes("FACEBOOK")) return messengerIcon;
  // Default to WhatsApp for WHATSAPP, SMS, and unknown
  return whatsappIcon;
};

const AdvancedAnalyticsDashboard = ({ companyId, selectedSpace, companyName, showTemplateAnalysis = false, onNavigateToTemplates }) => {
  const [activeTab, setActiveTab] = useState("clients");
  const [selectedChannelId, setSelectedChannelId] = useState("");
  const [isExporting, setIsExporting] = useState(false);

  const tabs = useMemo(
    () => (showTemplateAnalysis ? [...BASE_TABS, TEMPLATE_TAB] : BASE_TABS),
    [showTemplateAnalysis]
  );

  const handleTabClick = (tabId) => {
    if (tabId === "plantillas") {
      if (onNavigateToTemplates) onNavigateToTemplates();
      return;
    }
    setActiveTab(tabId);
  };

  const [startDate, setStartDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));

  const allChannelIds = useMemo(
    () => (selectedSpace?.channels?.length ? selectedSpace.channels.map((c) => c.id) : undefined),
    [selectedSpace]
  );

  // When a specific channel is selected, pass it as a single-element array (backend limitation).
  // When selectedChannelId is empty, pass the full list derived from selectedSpace.
  const channelIds = useMemo(
    () => (selectedChannelId ? [selectedChannelId] : allChannelIds),
    [selectedChannelId, allChannelIds]
  );

  const { hasAnyPermission } = usePermission();
  const canViewAnyAnalytics = hasAnyPermission(ANALYTICS_PERMISSIONS_LIST);
  const dataEnrichment = useAnalyticsDataEnrichment(canViewAnyAnalytics ? companyId : null);

  // Canales visibles en el selector: activos o Messenger (puede estar inactivo pero ser relevante)
  const selectableChannels = useMemo(
    () =>
      (dataEnrichment.channels || []).filter(
        (ch) => ch.isActive === true || (ch.platform?.name || "").toUpperCase() === "MESSENGER"
      ),
    [dataEnrichment.channels]
  );

  // Inicializar con el primer canal filtrado en cuanto los canales están disponibles
  useEffect(() => {
    if (selectableChannels.length > 0 && !selectedChannelId) {
      setSelectedChannelId(selectableChannels[0].id);
    }
  }, [selectableChannels]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInitialDate = (date) => {
    // `date` may be a Date object (from the calendar picker) or a string.
    // moment(date) parses Date objects in local time, so format() gives the
    // correct local YYYY-MM-DD string without any UTC shift.
    const parsed = moment.isMoment(date) ? date : moment(date);
    if (parsed.isValid()) setStartDate(parsed.format("YYYY-MM-DD"));
  };
  const handleEndDate = (date) => {
    const parsed = moment.isMoment(date) ? date : moment(date);
    if (parsed.isValid()) setEndDate(parsed.format("YYYY-MM-DD"));
  };

  const handleExport = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      // Timezone offset in minutes (e.g. -360 for UTC-6 / Mexico City).
      // new Date().getTimezoneOffset() returns positive for west-of-UTC zones,
      // but our backend subtracts the offset, so we negate it here.
      const timezoneOffset = -new Date().getTimezoneOffset();
      const channelId = selectedChannelId || undefined;

      // Fetch all data in parallel: conversation rows + agent metrics
      const [
        conversationResponse,
        topUsersResponse,
        assignedConvResponse,
        agentPerfResponse,
      ] = await Promise.all([
        analyticsDashboardApi.getExportReportClients({
          companyId,
          startDate,
          endDate,
          channelId,
          timezoneOffset,
        }),
        analyticsDashboardApi.getClickHouseMaterializedView("daily_user_stats", {
          companyId,
          startDate,
          endDate,
          channelId,
          timezoneOffset,
          limit: 5000,
        }),
        analyticsDashboardApi.getAssignedConversations({
          companyId,
          startDate,
          endDate,
          channelId,
          timezoneOffset,
        }),
        analyticsDashboardApi.getClickHouseMaterializedView("agent_performance_daily", {
          companyId,
          startDate,
          endDate,
          channelId,
          timezoneOffset,
          limit: 10000,
        }),
      ]);

      const conversationRows = conversationResponse?.data || [];

      // Build topUsersData — identical logic to UserPerformanceWidgets.topUsersData
      const rawTopUsers = topUsersResponse?.data ?? topUsersResponse ?? [];
      const userMap = new Map();
      rawTopUsers.forEach((item) => {
        const userId = item.user_id;
        if (!userMap.has(userId)) {
          userMap.set(userId, { userId, outgoingMessages: 0, finishedClients: 0, avgResponseTime: 0, responsesAfterBot: 0, totalEntries: 0 });
        }
        const u = userMap.get(userId);
        u.outgoingMessages += item.outgoing_messages || 0;
        u.finishedClients += item.finished_clients || 0;
        u.responsesAfterBot += item.responses_after_bot || 0;
        u.avgResponseTime += item.avg_response_time_seconds || 0;
        u.totalEntries += 1;
      });
      const usersData = Array.from(userMap.values())
        .map((u) => ({
          ...u,
          avgResponseTime: u.totalEntries > 0 ? u.avgResponseTime / u.totalEntries : 0,
          userName: dataEnrichment?.getUserName(u.userId) || `User ${u.userId.slice(-6)}`,
        }))
        .sort((a, b) => b.outgoingMessages - a.outgoingMessages);

      // Build assignedMap — agentId → assignedConversations
      const assignedMap = new Map();
      (assignedConvResponse?.data || []).forEach((item) => {
        assignedMap.set(item.agentId, item.assignedConversations);
      });

      // Build agentRatingsMap — agentId → rating % (identical to UserPerformanceWidgets)
      const rawAgentPerf = agentPerfResponse?.data ?? agentPerfResponse ?? [];
      const agentAccum = new Map();
      rawAgentPerf.forEach((item) => {
        const agentId = item.finished_by;
        if (!agentId) return;
        if (!agentAccum.has(agentId)) agentAccum.set(agentId, { totalRating: 0, totalFinished: 0 });
        const ag = agentAccum.get(agentId);
        const dayFinished = item.total_finished || 0;
        ag.totalRating += (item.avg_rating || 0) * dayFinished;
        ag.totalFinished += dayFinished;
      });
      const agentRatingsMap = new Map();
      agentAccum.forEach((data, agentId) => {
        if (data.totalFinished > 0) {
          agentRatingsMap.set(agentId, (data.totalRating / data.totalFinished / 5) * 100);
        }
      });

      await downloadAnalyticsReport({
        startDate,
        endDate,
        conversationRows,
        usersData,
        assignedMap,
        agentRatingsMap,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[AdvancedAnalyticsDashboard] Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }, [companyId, startDate, endDate, selectedChannelId, isExporting, dataEnrichment]);

  return (
    <>
      <Banner gradient={gradients.indigo}>
        <BannerContent>
          <BannerTitle>Panel de Estadísticas</BannerTitle>
          <BannerDescription>
            Analiza el rendimiento de tu equipo y conversaciones
            {companyName ? ` de ${companyName}` : ""}
          </BannerDescription>
        </BannerContent>
        <BannerImage src={estadisticasCharacter} alt="Estadísticas" />
      </Banner>

      <FilterToolbar>
        <FilterGroup>
          <FilterLabel>Periodo:</FilterLabel>
          <DatePickerColumn>
            <DatePickerWrapper>
              <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  autoOk
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="advanced-date-picker-init"
                  value={moment(startDate, "YYYY-MM-DD").toDate()}
                  maxDate={moment(endDate, "YYYY-MM-DD").toDate()}
                  onChange={handleInitialDate}
                  KeyboardButtonProps={{ "aria-label": "change date" }}
                />
                <DateArrow>→</DateArrow>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  autoOk
                  format="dd/MM/yyyy"
                  margin="normal"
                  id="advanced-date-picker-end"
                  value={moment(endDate, "YYYY-MM-DD").toDate()}
                  minDate={moment(startDate, "YYYY-MM-DD").toDate()}
                  maxDate={moment().toDate()}
                  onChange={handleEndDate}
                  KeyboardButtonProps={{ "aria-label": "change date" }}
                />
              </MuiPickersUtilsProvider>
            </DatePickerWrapper>
            <DateRangeHelperText>Ambas fechas son inclusivas</DateRangeHelperText>
          </DatePickerColumn>
        </FilterGroup>

        {selectableChannels.length > 0 && (
          <FilterGroup>
            <FilterLabel htmlFor="channel-select">Canal:</FilterLabel>
            <ChannelMuiSelectWrapper>
              <Select
                id="channel-select"
                value={selectedChannelId}
                onChange={(e) => setSelectedChannelId(e.target.value)}
                disableUnderline
                renderValue={(value) => {
                  const ch = selectableChannels.find((c) => c.id === value);
                  if (!ch) return "Seleccionar canal";
                  return (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <img src={getPlatformIcon(ch)} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
                      {ch.name || ch.id}
                    </span>
                  );
                }}
              >
                {selectableChannels.map((ch) => (
                  <MenuItem key={ch.id} value={ch.id}>
                    <img src={getPlatformIcon(ch)} alt="" style={{ width: 18, height: 18, objectFit: "contain", marginRight: 8 }} />
                    {ch.name || ch.id}
                  </MenuItem>
                ))}
              </Select>
            </ChannelMuiSelectWrapper>
          </FilterGroup>
        )}

        <FilterGroup>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 16px",
              borderRadius: 8,
              border: "1.5px solid #6366f1",
              background: isExporting ? "#e0e7ff" : "#6366f1",
              color: isExporting ? "#6366f1" : "#fff",
              fontWeight: 600,
              fontSize: 13,
              cursor: isExporting ? "not-allowed" : "pointer",
              transition: "background 0.2s",
            }}
          >
            {isExporting ? "Exportando…" : "⬇ Exportar reporte"}
          </button>
        </FilterGroup>
      </FilterToolbar>

      <TabsNavigation>
        {tabs.map((tab) => (
          <TabPill key={tab.id} active={activeTab === tab.id} onClick={() => handleTabClick(tab.id)}>
            <tab.Icon style={{ marginRight: 6 }} />
            {tab.label}
          </TabPill>
        ))}
      </TabsNavigation>

      {activeTab === "clients" && (
        <PermissionGate
          permission={ANALYTICS_WIDGET_PERMISSIONS.CLIENTS_SUMMARY.permission}
          fallback={NoPermissionFallback}
        >
          <ClientAnalyticsWidgets
            companyId={companyId}
            startDate={startDate}
            endDate={endDate}
            channelIds={channelIds}
            dataEnrichment={dataEnrichment}
          />
        </PermissionGate>
      )}

      {activeTab === "messages" && (
        <PermissionGate
          permission={ANALYTICS_WIDGET_PERMISSIONS.MESSAGES_SUMMARY.permission}
          fallback={NoPermissionFallback}
        >
          <MessageAnalyticsWidgets
            companyId={companyId}
            startDate={startDate}
            endDate={endDate}
            channelIds={channelIds}
            getChannelName={dataEnrichment.getChannelName}
          />
        </PermissionGate>
      )}

      {activeTab === "users" && (
        <PermissionGate
          permission={ANALYTICS_WIDGET_PERMISSIONS.AGENTS_PERFORMANCE.permission}
          fallback={NoPermissionFallback}
        >
          <UserPerformanceWidgets
            companyId={companyId}
            startDate={startDate}
            endDate={endDate}
            channelIds={channelIds}
            dataEnrichment={dataEnrichment}
          />
        </PermissionGate>
      )}

      {activeTab === "campaigns" && (
        <ClientCampaignAnalyticsDashboard
          companyId={companyId}
          startDate={startDate}
          endDate={endDate}
          channelIds={channelIds}
        />
      )}
    </>
  );
};

export default AdvancedAnalyticsDashboard;
